import React, { lazy, Suspense } from "react";
import { RouteObject, Navigate, redirect } from "react-router";
import { RouteLoader } from "@/shared/ui/RouteLoader";

// Eager load frequently accessed core pages
import Dashboard from "@/features/dashboard/pages";
import OrganizationSettingsMembers from "@/features/organization/pages/OrganizationMembersPage";
import PersonProfileScreen from "@/features/people/pages/PersonProfilePage";
import Projects from "@/features/projects/pages/ProjectsListPage";
import ProjectKanban from "@/features/projects/pages/ProjectKanbanPage";
import CandidateProfile from "@/features/projects/pages/ProjectCandidatePage";
import Teams from "@/features/teams/pages/TeamsListPage";
import TeamProfileScreen from "@/features/teams/pages/team/TeamProfilePage";
import MemberInTeamProfileScreen from "@/features/teams/pages/team/member/MemberInTeamProfilePage";
import {
  RequireProjectsListAccess,
  RequireTeamsListAccess,
} from "@/shared/utils/router/RequireOrgListAccess";
import { RequireResourceAccess } from "@/shared/utils/router/RequireResourceAccess";
import { RequireScorecardsAccess } from "@/shared/utils/router/RequireScorecardsAccess";
import { AccessLevel } from "@/shared/constants";
import { ResourceEnum } from "@/shared/constants/resources";

// Lazy load settings and less frequently accessed pages
const OrganizationSettings = lazy(
  () => import("@/features/organization/pages/OrganizationSettingsPage")
);
const ProjectSettings = lazy(
  () => import("@/features/projects/pages/ProjectSettingsPage")
);
const ProjectScorecardEdit = lazy(
  () => import("@/features/scorecards/pages/edit/ProjectScorecardEditPage")
);
const ProjectScorecardCompetenciesEdit = lazy(
  () =>
    import(
      "@/features/scorecards/pages/competencies/ProjectCompetenciesEditPage"
    )
);
const Collaborators = lazy(
  () => import("@/features/projects/pages/ProjectCollaboratorsPage")
);
const ProjectCompare = lazy(
  () => import("@/features/projects/pages/ProjectComparePage")
);
const ProjectAssessment = lazy(
  () => import("@/features/assessments/pages/AssessmentViewPage")
);
const AssessmentPageV2 = lazy(
  () => import("@/features/assessments/pages/AssessmentPage")
);
const CreateAssessmentRedirect = lazy(
  () => import("@/features/assessments/pages/AssessmentCreatePage")
);
const AssessmentReport = lazy(
  () => import("@/features/assessments/pages/AssessmentReportPage")
);
const Scorecards = lazy(
  () => import("@/features/scorecards/pages/ScorecardsListPage")
);
const ScorecardEdit = lazy(
  () => import("@/features/scorecards/pages/edit/ScorecardEditPage")
);
const ScorecardCompetenciesEdit = lazy(
  () => import("@/features/scorecards/pages/competencies/CompetenciesPage")
);
const TeamSettingsScreen = lazy(
  () => import("@/features/teams/pages/team/TeamSettingsPage")
);

export const organisationRoutes: RouteObject[] = [
  {
    path: "org/:orgId",
    loader: ({ params }) => {
      // Check if the segment is literally the string "undefined"
      if (params.orgId === "undefined") {
        const fallbackOrg = localStorage.getItem("recentOrg");

        // If no fallbackOrg then navigate to org select
        if (!fallbackOrg) {
          return redirect("/org");
        }

        // Return a redirect response immediately to avoid navigating to undefined route
        return redirect(`/org/${fallbackOrg}/dashboard`);
      }

      // Otherwise, proceed as normal (fetch data if needed)
      return null;
    },
    children: [
      {
        index: true,
        element: <Navigate to="dashboard" replace />,
      },
      {
        path: "dashboard",
        Component: Dashboard,
      },
      {
        path: "settings",
        element: (
          <Suspense
            fallback={<RouteLoader messageKey="common.loading_settings" />}
          >
            <OrganizationSettings />
          </Suspense>
        ),
      },
      {
        path: "members",
        Component: OrganizationSettingsMembers,
      },
      {
        path: "person/:memberId",
        Component: PersonProfileScreen,
      },
      {
        path: "projects",
        element: (
          <RequireProjectsListAccess>
            <Projects />
          </RequireProjectsListAccess>
        ),
      },
      {
        path: "projects/:projectId",
        element: (
          <RequireResourceAccess
            resourceType={ResourceEnum.project}
            accessLevel={AccessLevel.read}
            paramKey="projectId"
          />
        ),
        children: [
          {
            index: true,
            Component: ProjectKanban,
          },
          {
            path: "settings",
            Component: ProjectSettings,
          },
          {
            path: "scorecard",
            Component: ProjectScorecardEdit,
          },
          {
            path: "scorecard/competencies",
            Component: ProjectScorecardCompetenciesEdit,
          },
          {
            path: "collaborators",
            Component: Collaborators,
          },
          {
            path: "compare",
            Component: ProjectCompare,
          },
          {
            path: "person/:personId",
            Component: CandidateProfile,
          },
          {
            path: "person/:personId/assessment/new",
            Component: CreateAssessmentRedirect,
          },
          {
            path: "person/:personId/assessment/:assessmentId",
            Component: ProjectAssessment,
          },
          {
            path: "person/:personId/assessment/:assessmentId/report",
            Component: AssessmentReport,
          },
          {
            path: "person/:personId/assessment/:assessmentId/v2",
            Component: AssessmentPageV2,
          },
        ],
      },
      {
        path: "scorecards",
        element: (
          <RequireScorecardsAccess>
            <Suspense
              fallback={<RouteLoader messageKey="common.loading_scorecards" />}
            >
              <Scorecards />
            </Suspense>
          </RequireScorecardsAccess>
        ),
      },
      {
        path: "scorecards/:scorecardId/edit",
        element: (
          <RequireScorecardsAccess>
            <Suspense
              fallback={<RouteLoader messageKey="common.loading_scorecards" />}
            >
              <ScorecardEdit />
            </Suspense>
          </RequireScorecardsAccess>
        ),
      },
      {
        path: "scorecards/:scorecardId/competencies",
        element: (
          <RequireScorecardsAccess>
            <Suspense
              fallback={<RouteLoader messageKey="common.loading_scorecards" />}
            >
              <ScorecardCompetenciesEdit />
            </Suspense>
          </RequireScorecardsAccess>
        ),
      },
      {
        path: "teams",
        element: (
          <RequireTeamsListAccess>
            <Teams />
          </RequireTeamsListAccess>
        ),
      },
      {
        path: "teams/:teamId",
        element: (
          <RequireResourceAccess
            resourceType={ResourceEnum.team}
            accessLevel={AccessLevel.read}
            paramKey="teamId"
          />
        ),
        children: [
          {
            index: true,
            Component: TeamProfileScreen,
          },
          {
            path: "settings",
            Component: TeamSettingsScreen,
          },
          {
            path: "member/:memberId",
            Component: MemberInTeamProfileScreen,
          },
        ],
      },
    ],
  },
];
